import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Calendar, Clock, Home, User, Users, FileText, Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { viewings, properties, leads, agents, type Property, type Lead, type Agent } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface CreateViewingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultDate?: Date;
}

interface FormData {
  property_id: string;
  lead_id: string;
  agent_id: string;
  scheduled_date: string;
  scheduled_time: string;
  duration_minutes: number;
  notes: string;
}

const DURATION_OPTIONS = [
  { value: 15, label: '15 mínútur' },
  { value: 30, label: '30 mínútur' },
  { value: 45, label: '45 mínútur' },
  { value: 60, label: '60 mínútur' },
];

export function CreateViewingModal({ open, onOpenChange, defaultDate }: CreateViewingModalProps) {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<FormData>({
    property_id: '',
    lead_id: '',
    agent_id: '',
    scheduled_date: defaultDate ? format(defaultDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
    scheduled_time: '10:00',
    duration_minutes: 30,
    notes: '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        property_id: '',
        lead_id: '',
        agent_id: '',
        scheduled_date: defaultDate ? format(defaultDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
        scheduled_time: '10:00',
        duration_minutes: 30,
        notes: '',
      });
      setErrors({});
    }
  }, [open, defaultDate]);

  // Fetch properties
  const { data: propertiesData, isLoading: propertiesLoading } = useQuery({
    queryKey: ['properties', 'active'],
    queryFn: () => properties.list(token!, { status: 'á_markaði', limit: 100 }),
    enabled: !!token && open,
  });

  // Fetch leads
  const { data: leadsData, isLoading: leadsLoading } = useQuery({
    queryKey: ['leads', 'all'],
    queryFn: () => leads.list(token!, { limit: 100 }),
    enabled: !!token && open,
  });

  // Fetch agents
  const { data: agentsData, isLoading: agentsLoading } = useQuery({
    queryKey: ['agents'],
    queryFn: () => agents.list(token!),
    enabled: !!token && open,
  });

  // Create viewing mutation
  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof viewings.create>[1]) => viewings.create(token!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['viewings'] });
      onOpenChange(false);
    },
  });

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.property_id) {
      newErrors.property_id = 'Veldu eign';
    }
    if (!formData.lead_id) {
      newErrors.lead_id = 'Veldu viðskiptavin';
    }
    if (!formData.agent_id) {
      newErrors.agent_id = 'Veldu söluráðgjafa';
    }
    if (!formData.scheduled_date) {
      newErrors.scheduled_date = 'Veldu dagsetningu';
    }
    if (!formData.scheduled_time) {
      newErrors.scheduled_time = 'Veldu tíma';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Combine date and time into ISO string
    const scheduledAt = new Date(`${formData.scheduled_date}T${formData.scheduled_time}:00`).toISOString();

    createMutation.mutate({
      property_id: formData.property_id,
      lead_id: formData.lead_id,
      agent_id: formData.agent_id,
      scheduled_at: scheduledAt,
      duration_minutes: formData.duration_minutes,
      notes: formData.notes || undefined,
      status: 'bókað',
    });
  };

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const isLoading = propertiesLoading || leadsLoading || agentsLoading;
  const activeProperties = propertiesData?.data || [];
  const allLeads = leadsData?.data || [];
  const allAgents = agentsData?.data?.filter((a: Agent) => a.active) || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-prima-600" />
            Bóka skoðun
          </DialogTitle>
          <DialogDescription>
            Skráðu nýja skoðun á eign fyrir viðskiptavin.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Property selector */}
          <div className="space-y-2">
            <Label htmlFor="property" className="flex items-center gap-2">
              <Home className="h-4 w-4 text-gray-500" />
              Eign
            </Label>
            <Select
              value={formData.property_id}
              onValueChange={(value) => updateField('property_id', value)}
              disabled={isLoading}
            >
              <SelectTrigger id="property" className={errors.property_id ? 'border-red-500' : ''}>
                <SelectValue placeholder="Veldu eign..." />
              </SelectTrigger>
              <SelectContent>
                {activeProperties.map((property: Property) => (
                  <SelectItem key={property.id} value={property.id}>
                    {property.address}
                    {property.city && `, ${property.city}`}
                  </SelectItem>
                ))}
                {activeProperties.length === 0 && !propertiesLoading && (
                  <SelectItem value="__no_properties__" disabled>
                    Engar eignir á markaði
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {errors.property_id && (
              <p className="text-sm text-red-500">{errors.property_id}</p>
            )}
          </div>

          {/* Lead selector */}
          <div className="space-y-2">
            <Label htmlFor="lead" className="flex items-center gap-2">
              <User className="h-4 w-4 text-gray-500" />
              Viðskiptavinur
            </Label>
            <Select
              value={formData.lead_id}
              onValueChange={(value) => updateField('lead_id', value)}
              disabled={isLoading}
            >
              <SelectTrigger id="lead" className={errors.lead_id ? 'border-red-500' : ''}>
                <SelectValue placeholder="Veldu viðskiptavin..." />
              </SelectTrigger>
              <SelectContent>
                {allLeads.map((lead: Lead) => (
                  <SelectItem key={lead.id} value={lead.id}>
                    {lead.caller_name || 'Nafnlaus'} - {lead.caller_phone}
                  </SelectItem>
                ))}
                {allLeads.length === 0 && !leadsLoading && (
                  <SelectItem value="__no_leads__" disabled>
                    Engar ábendingar
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {errors.lead_id && (
              <p className="text-sm text-red-500">{errors.lead_id}</p>
            )}
          </div>

          {/* Agent selector */}
          <div className="space-y-2">
            <Label htmlFor="agent" className="flex items-center gap-2">
              <Users className="h-4 w-4 text-gray-500" />
              Söluráðgjafi
            </Label>
            <Select
              value={formData.agent_id}
              onValueChange={(value) => updateField('agent_id', value)}
              disabled={isLoading}
            >
              <SelectTrigger id="agent" className={errors.agent_id ? 'border-red-500' : ''}>
                <SelectValue placeholder="Veldu söluráðgjafa..." />
              </SelectTrigger>
              <SelectContent>
                {allAgents.map((agent: Agent) => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </SelectItem>
                ))}
                {allAgents.length === 0 && !agentsLoading && (
                  <SelectItem value="__no_agents__" disabled>
                    Engir virkir söluráðgjafar
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {errors.agent_id && (
              <p className="text-sm text-red-500">{errors.agent_id}</p>
            )}
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date" className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-500" />
                Dagsetning
              </Label>
              <Input
                id="date"
                type="date"
                value={formData.scheduled_date}
                onChange={(e) => updateField('scheduled_date', e.target.value)}
                min={format(new Date(), 'yyyy-MM-dd')}
                className={errors.scheduled_date ? 'border-red-500' : ''}
              />
              {errors.scheduled_date && (
                <p className="text-sm text-red-500">{errors.scheduled_date}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="time" className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-500" />
                Tími
              </Label>
              <Input
                id="time"
                type="time"
                value={formData.scheduled_time}
                onChange={(e) => updateField('scheduled_time', e.target.value)}
                className={errors.scheduled_time ? 'border-red-500' : ''}
              />
              {errors.scheduled_time && (
                <p className="text-sm text-red-500">{errors.scheduled_time}</p>
              )}
            </div>
          </div>

          {/* Duration */}
          <div className="space-y-2">
            <Label htmlFor="duration">Lengd skoðunar</Label>
            <Select
              value={String(formData.duration_minutes)}
              onValueChange={(value) => updateField('duration_minutes', Number(value))}
            >
              <SelectTrigger id="duration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATION_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-500" />
              Athugasemdir
            </Label>
            <Textarea
              id="notes"
              placeholder="Sérstakar óskir eða athugasemdir..."
              value={formData.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              rows={3}
            />
          </div>

          {/* Error message */}
          {createMutation.isError && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              Villa kom upp við að búa til skoðun. Reyndu aftur.
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createMutation.isPending}
            >
              Hætta við
            </Button>
            <Button type="submit" disabled={createMutation.isPending || isLoading}>
              {createMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Bóka...
                </>
              ) : (
                'Bóka skoðun'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
